import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
  type KeyboardEvent,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { IndustryGroup, IndustryInfo } from "../../data/industryGroups";

interface SearchableSelectProps {
  id: string;
  groups: IndustryGroup[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

function matchesSearch(item: IndustryInfo, query: string): boolean {
  const q = query.toLowerCase();
  return (
    item.label.toLowerCase().includes(q) ||
    item.value.toLowerCase().includes(q) ||
    item.description.toLowerCase().includes(q)
  );
}

export function SearchableSelect({
  id,
  groups,
  value,
  onChange,
  disabled = false,
  placeholder = "Search industries...",
}: SearchableSelectProps): ReactNode {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Find the currently selected label
  const selectedItem = groups.flatMap((g) => g.industries).find((i) => i.value === value);
  const selectedLabel = selectedItem?.label ?? "";

  // Filter groups based on search
  const filteredGroups = groups
    .map((group) => ({
      ...group,
      industries: group.industries.filter((i) => matchesSearch(i, search)),
    }))
    .filter((group) => group.industries.length > 0);

  // Flat list of visible items for keyboard navigation
  const flatFiltered = filteredGroups.flatMap((g) => g.industries);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll("[data-option]");
      const item = items[highlightIndex];
      if (item && typeof item.scrollIntoView === "function") {
        item.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightIndex]);

  const selectItem = useCallback(
    (itemValue: string) => {
      onChange(itemValue);
      setOpen(false);
      setSearch("");
      setHighlightIndex(-1);
    },
    [onChange],
  );

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, flatFiltered.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const item = flatFiltered[highlightIndex];
      if (item) {
        selectItem(item.value);
      }
    } else if (event.key === "Escape") {
      setOpen(false);
      setSearch("");
    }
  }

  function handleInputFocus(): void {
    if (!disabled) {
      setOpen(true);
      setHighlightIndex(-1);
    }
  }

  return (
    <div className="searchable-select" ref={containerRef}>
      <div
        className={`searchable-select-trigger${open ? " searchable-select-open" : ""}`}
        onClick={() => {
          if (!disabled) {
            setOpen(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
      >
        {open ? (
          <input
            ref={inputRef}
            id={id}
            type="text"
            className="searchable-select-input"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setHighlightIndex(-1);
            }}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-controls={`${id}-listbox`}
            aria-autocomplete="list"
          />
        ) : (
          <button
            type="button"
            className="searchable-select-display"
            id={id}
            disabled={disabled}
            aria-haspopup="listbox"
          >
            <span>{selectedLabel || placeholder}</span>
            <span className="searchable-select-chevron" aria-hidden="true">
              &#9662;
            </span>
          </button>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="searchable-select-dropdown"
            ref={listRef}
            role="listbox"
            id={`${id}-listbox`}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
          >
            {filteredGroups.length === 0 && (
              <div className="searchable-select-empty">No industries found</div>
            )}
            {filteredGroups.map((group) => (
              <div key={group.name} className="searchable-select-group">
                <div className="searchable-select-group-header">{group.name}</div>
                {group.industries.map((industry) => {
                  const flatIndex = flatFiltered.indexOf(industry);
                  const isHighlighted = flatIndex === highlightIndex;
                  const isSelected = industry.value === value;
                  return (
                    <button
                      key={industry.value}
                      type="button"
                      data-option
                      role="option"
                      aria-selected={isSelected}
                      className={
                        "searchable-select-option" +
                        (isHighlighted ? " highlighted" : "") +
                        (isSelected ? " selected" : "")
                      }
                      onMouseEnter={() => setHighlightIndex(flatIndex)}
                      onClick={() => selectItem(industry.value)}
                    >
                      <div className="searchable-select-option-label">{industry.label}</div>
                      <div className="searchable-select-option-desc">{industry.description}</div>
                    </button>
                  );
                })}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
